import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getCommandCenter, createPostHuddleAction, getTeamMembers } from '../../lib/api';
import { StatusChip, TONE } from './ui';
import { X, ArrowRight, ArrowLeft, CheckCircle, Play, PauseCircle, Clock, Users, Sparkle, ListChecks } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STORAGE_KEY = 'huddle_session_v1';

/*
 Huddle Mode = focused meeting overlay.
 - Loads the Command Center queue as the meeting agenda.
 - Walks the operator through each item one-by-one.
 - Captures decisions/assignments/notes per item.
 - Writes accepted actions into post_huddle_actions (existing endpoint).
 - Saves full session locally so the Recap page can auto-build itself.
*/

const OUTCOMES = [
  { key: 'assigned', label: 'Assign & Own', tone: 'info', icon: Users },
  { key: 'resolved', label: 'Resolved Now', tone: 'good', icon: CheckCircle },
  { key: 'carry', label: 'Carry Forward', tone: 'high', icon: Clock },
  { key: 'skip', label: 'Skip', tone: 'neutral', icon: X },
];

export default function HuddleMode({ open, onClose, currentUser }) {
  const [queue, setQueue] = useState([]);
  const [members, setMembers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState({});
  const [note, setNote] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [deadline, setDeadline] = useState('End of day');
  const [started, setStarted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [showRecap, setShowRecap] = useState(false);
  const [tick, setTick] = useState(0);
  const [itemStartedAt, setItemStartedAt] = useState(null);

  // Timer tick every second when huddle running
  useEffect(() => {
    if (!started || showRecap) return;
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [started, showRecap]);
  useEffect(() => { if (started) setItemStartedAt(new Date()); }, [idx, started]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [cc, m] = await Promise.all([getCommandCenter(), getTeamMembers()]);
      setQueue(cc.queue || []);
      setMembers(m);
    } catch (e) { toast.error('Failed to load huddle data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadQueue();
    setIdx(0); setDecisions({}); setNote(''); setAssignTo(''); setDeadline('End of day');
    setShowRecap(false); setStarted(false); setStartedAt(null);
  }, [open, loadQueue]);

  const current = queue[idx];

  const recordDecision = async (outcome) => {
    const record = {
      idx, outcome, note, deadline, assign_to: assignTo || (current?.owner || ''),
      borrower: current?.borrower, issue: current?.issue, category: current?.category,
      recorded_at: new Date().toISOString(),
    };
    setDecisions(p => ({ ...p, [idx]: record }));
    // Persist to backend for assign/resolve/carry outcomes
    if (outcome === 'assigned' || outcome === 'carry' || outcome === 'resolved') {
      try {
        await createPostHuddleAction({
          borrower: current?.borrower || 'General',
          action: (current?.issue || '') + (note ? ` — ${note}` : ''),
          assigned_to: record.assign_to || '',
          deadline: outcome === 'resolved' ? 'Completed' : deadline,
        });
      } catch (e) { /* non-blocking */ }
    }
    // Move forward
    setNote(''); setAssignTo(''); setDeadline('End of day');
    if (idx + 1 < queue.length) setIdx(idx + 1);
    else setShowRecap(true);
  };

  const finishHuddle = () => {
    // Save session for Recap auto-generation
    const session = {
      started_at: startedAt, ended_at: new Date().toISOString(),
      operator: currentUser?.name || 'Unknown',
      queue_count: queue.length,
      decisions,
      items: queue,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(session)); } catch(e) { /* noop */ }
    toast.success('Huddle recap saved — visit the Recap tab');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="huddle-mode-overlay">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E63935]/20 border border-[#E63935]/30 flex items-center justify-center">
              <Play size={18} weight="fill" className="text-[#E63935]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Huddle Mode</h1>
              <p className="text-xs text-muted-foreground">Focused meeting flow · one item at a time</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg border border-border hover:bg-muted flex items-center justify-center" data-testid="huddle-close-btn">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>
        ) : !started ? (
          /* Pre-huddle briefing */
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-6">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Today&apos;s Agenda</p>
              <p className="text-4xl font-bold text-foreground mt-1">{queue.length} items</p>
              <p className="text-sm text-muted-foreground mt-2">Prioritized by impact and urgency across Hot Files, Commitments, Escalations and Client Sentiment.</p>
              <div className="mt-4 space-y-1.5 max-h-72 overflow-y-auto pr-2">
                {queue.slice(0, 10).map((q, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className={`w-1.5 h-6 rounded-full ${TONE[q.priority]?.dot || TONE.neutral.dot}`} />
                    <span className="text-foreground font-semibold w-32 truncate">{q.borrower}</span>
                    <span className="text-muted-foreground flex-1 truncate">{q.issue}</span>
                    <StatusChip tone={q.priority}>{q.due_label}</StatusChip>
                  </div>
                ))}
                {queue.length > 10 && <p className="text-[11px] text-muted-foreground text-center pt-2">+ {queue.length - 10} more...</p>}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="h-10 border-border">Cancel</Button>
              <Button onClick={() => { setStarted(true); setStartedAt(new Date().toISOString()); }} disabled={queue.length === 0}
                className="h-10 bg-[#E63935] hover:bg-[#d32f2f] text-white" data-testid="begin-huddle-btn">
                <Play size={14} weight="fill" className="mr-1.5" /> Begin Huddle
              </Button>
            </div>
          </div>
        ) : showRecap ? (
          /* Recap view */
          <div className="space-y-4" data-testid="huddle-recap">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                <h2 className="text-lg font-bold text-foreground">Huddle Complete</h2>
              </div>
              <p className="text-xs text-muted-foreground mt-1">All {queue.length} items reviewed. The Recap page will auto-build from this session.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['assigned', 'resolved', 'carry', 'skip'].map(o => {
                const count = Object.values(decisions).filter(d => d.outcome === o).length;
                const cfg = OUTCOMES.find(x => x.key === o);
                return (
                  <div key={o} className="rounded-lg border border-border bg-card p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">{count}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{cfg.label}</p>
                  </div>
                );
              })}
            </div>
            <div className="rounded-lg border border-border bg-card p-4 max-h-72 overflow-y-auto">
              <h3 className="text-sm font-bold text-foreground mb-2">Decisions</h3>
              {Object.values(decisions).map((d, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
                  <StatusChip tone={OUTCOMES.find(o => o.key === d.outcome)?.tone || 'neutral'}>{OUTCOMES.find(o => o.key === d.outcome)?.label}</StatusChip>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{d.borrower}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{d.issue}</p>
                    {d.note && <p className="text-[11px] text-foreground/70 italic mt-0.5">&ldquo;{d.note}&rdquo;</p>}
                  </div>
                  {d.assign_to && <span className="text-[10px] font-bold text-foreground bg-muted px-1.5 py-0.5 rounded">{d.assign_to}</span>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={finishHuddle} className="h-10 bg-[#E63935] hover:bg-[#d32f2f] text-white" data-testid="finish-huddle-btn">
                <ListChecks size={14} className="mr-1.5" /> Save & Close
              </Button>
            </div>
          </div>
        ) : (
          /* Item review flow */
          <div className="space-y-4" data-testid="huddle-item-review">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Item {idx + 1} of {queue.length}</span>
              <div className="flex-1 mx-4 h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[#E63935]" style={{ width: `${((idx + 1) / queue.length) * 100}%` }} />
              </div>
              <span>{Object.keys(decisions).length} decisions</span>
            </div>
            {/* Huddle timer */}
            {startedAt && (() => {
              const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
              const itemElapsed = itemStartedAt ? Math.floor((Date.now() - itemStartedAt.getTime()) / 1000) : 0;
              const totalMax = 15 * 60;
              const overItem = itemElapsed > 90;
              const overTotal = elapsed > totalMax;
              const mm = Math.floor(elapsed / 60), ss = elapsed % 60;
              return (
                <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${overTotal ? 'border-red-500/40 bg-red-500/10' : overItem ? 'border-amber-500/40 bg-amber-500/10' : 'border-border bg-card'}`} data-testid="huddle-timer">
                  <div className="flex items-center gap-3">
                    <span className={`font-mono font-bold tabular-nums text-sm ${overTotal ? 'text-red-500' : overItem ? 'text-amber-500' : 'text-foreground'}`}>{String(mm).padStart(2,'0')}:{String(ss).padStart(2,'0')}</span>
                    <span className="text-muted-foreground">/ 15:00 huddle</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] font-semibold ${overItem ? 'text-amber-500' : 'text-muted-foreground'}`}>Item: {itemElapsed}s</span>
                    {overItem && <span className="text-[11px] font-bold text-amber-500 animate-pulse">Time to move on</span>}
                  </div>
                </div>
              );
            })()}
            {current && (
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <span className={`w-1.5 h-14 rounded-full ${TONE[current.priority]?.dot || TONE.neutral.dot} flex-shrink-0 mt-1`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h2 className="text-lg font-bold text-foreground">{current.borrower}</h2>
                      <StatusChip tone={current.priority}>{current.priority.toUpperCase()}</StatusChip>
                      <StatusChip tone="neutral">{current.category.replace('_', ' ').toUpperCase()}</StatusChip>
                    </div>
                    <p className="text-sm text-foreground mt-2">{current.issue}</p>
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                      {current.owner && <span className="text-xs text-muted-foreground">Current owner: <span className="text-foreground font-semibold">{current.owner}</span></span>}
                      <span className={`text-xs font-semibold ${TONE[current.priority]?.text}`}>Due: {current.due_label}</span>
                    </div>
                  </div>
                </div>

                {/* Decision inputs */}
                <div className="mt-5 space-y-3 border-t border-border pt-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Discussion note (optional)</label>
                    <Input value={note} onChange={e => setNote(e.target.value)} placeholder="What did the team decide?" className="mt-1 border-border" data-testid="huddle-note-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Assign to</label>
                      <Select value={assignTo} onValueChange={setAssignTo}>
                        <SelectTrigger className="mt-1 border-border h-10 text-sm" data-testid="huddle-assign-select"><SelectValue placeholder={current.owner || 'Team member'} /></SelectTrigger>
                        <SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} · {m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Deadline</label>
                      <Select value={deadline} onValueChange={setDeadline}>
                        <SelectTrigger className="mt-1 border-border h-10 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="End of day">End of day</SelectItem>
                          <SelectItem value="Tomorrow">Tomorrow</SelectItem>
                          <SelectItem value="This week">This week</SelectItem>
                          <SelectItem value="Next huddle">Next huddle</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Outcome buttons */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {OUTCOMES.map(o => {
                    const Icon = o.icon;
                    const t = TONE[o.tone];
                    return (
                      <button key={o.key} onClick={() => recordDecision(o.key)}
                        className={`rounded-lg border p-3 text-center transition-colors ${t.border} ${t.bg} hover:brightness-125`}
                        data-testid={`huddle-outcome-${o.key}`}>
                        <Icon size={18} weight="fill" className={`${t.text} mx-auto mb-1`} />
                        <p className={`text-xs font-bold ${t.text}`}>{o.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="h-9 border-border">
                <ArrowLeft size={14} className="mr-1" /> Previous
              </Button>
              <Button variant="outline" onClick={() => setShowRecap(true)} className="h-9 border-border" data-testid="huddle-end-early-btn">
                <PauseCircle size={14} className="mr-1.5" /> End Huddle
              </Button>
              <Button variant="outline" onClick={() => { recordDecision('skip'); }} className="h-9 border-border">
                Skip <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
